import {BookingSubscriptionStatus, EquipmentLengthsConstant} from "../components/constants/securspace-constants";
import {formatCurrencyValue} from "./PaymentUtils";
import EquipmentTypes, { LocationEquipmentType } from "../components/EquipmentTypes";
import moment from "moment";
import { DateFormats } from "../components/constants/securspace-constants";
import { SelectInputProps } from "../hooks/report/useGetOptions";
import {
  FrequencyOptions,
  FREQUENCY_TYPE_WEEKLY,
  FREQUENCY_TYPE_MONTHLY
} from "../controls/FrequencyOption";

export const RECURRING = "RECURRING";
export const ONE_TIME = "ONE_TIME";
export const DAILY = "DAILY";
export const MONTHLY = "MONTHLY";
export const WEEKLY = "WEEKLY";

type DurationType = 'DAILY' | 'WEEKLY' | 'MONTHLY';
type FrequencyType = 'RECURRING' | 'ONE_TIME';

interface Booking {
  frequency: FrequencyType;
  durationType: DurationType;
  rate: number;
  recurringSupplierPayoutAmount?: number;
  startDate: string;
  supplierLegalAgreementFileName?: string;
  locationAddressLine1: string;
  locationCity: string;
  locationState: string;
  locationZip: string;
}

interface Location {
  equipmentTypesAll?: LocationEquipmentType[];
  pricePerDay?: number;
  pricePerMonth?: number;
}

export const formatFrequency = (booking: Booking): string => {
  return booking.frequency === 'RECURRING' && booking.durationType === 'WEEKLY' ?
    'Recurring - Weekly' : booking.frequency === 'RECURRING' && booking.durationType === 'MONTHLY' ?
      'Recurring - Monthly' : 'One-Time'
}

export const formatFrequencyByDuration = (frequency: FrequencyType, duration: DurationType): string => {
  return frequency === 'RECURRING' && duration === 'WEEKLY' ?
    'Recurring - Weekly' : frequency === 'RECURRING' && duration === 'MONTHLY' ?
      'Recurring - Monthly' : 'One-Time'
}

export const formatEquipmentLength = (equipmentLength: string): string => {
  switch (equipmentLength) {
    case Object.keys(EquipmentLengthsConstant)[ 0 ]:
      return EquipmentLengthsConstant.TWENTY_FIVE_FOOT
    case Object.keys(EquipmentLengthsConstant)[ 1 ]:
      return EquipmentLengthsConstant.FORTY_FIVE_FOOT
    case Object.keys(EquipmentLengthsConstant)[ 2 ]:
      return EquipmentLengthsConstant.FIFTY_THREE_FOOT
    case Object.keys(EquipmentLengthsConstant)[ 3 ]:
      return EquipmentLengthsConstant.EIGHTY_THREE_FOOT
    default :
      return Object.keys(EquipmentLengthsConstant)[ 0 ]
  }
}

export const formatSubscriptionStatus = (bookingSubscriptionStatus: string): string => {
  switch (bookingSubscriptionStatus) {
    case Object.keys(BookingSubscriptionStatus)[ 0 ]:
      return BookingSubscriptionStatus.ACTIVE
    case Object.keys(BookingSubscriptionStatus)[ 1 ]:
      return BookingSubscriptionStatus.CANCELLED
    case Object.keys(BookingSubscriptionStatus)[ 2 ]:
      return BookingSubscriptionStatus.DECLINED
    case Object.keys(BookingSubscriptionStatus)[ 3 ]:
      return BookingSubscriptionStatus.NONE
    default:
      return BookingSubscriptionStatus.NONE;
  }
}

export const formatBookingRate = (booking: Booking): string => {
  switch (booking.durationType) {
    case 'WEEKLY' :
      return `${formatCurrencyValue(booking.rate, true)}/week`;
    case 'MONTHLY':
      return `${formatCurrencyValue(booking.rate, true)}/month`;
    case 'DAILY':
      return `${formatCurrencyValue(booking.rate, true)}/day`;
    default :
      return `${formatCurrencyValue(booking.rate, true)}/day`;
  }
}

export const formatActive = (value: boolean): string => {
  return value ? 'Active' : 'Not Active'
}

export const getRentalAgreementURL = (fileName?: string): string => {
  return fileName ?
      `https://s3-us-west-1.amazonaws.com/securspace-files/legal-agreements/${fileName}`
      :
      'https://static.secur.space/app-files/RESERVATION+AGREEMENT.pdf';
}

export const convertDuration = (duration: DurationType): number => {
  if (duration === MONTHLY) {
      return 30;
  } else if (duration === WEEKLY) {
      return 7;
  } else {
      return 0;
  }
}

export const calculateRate = (selectedAssetType: string, location: Location | null | undefined, monthly: boolean): number => {
  const selectedEq = EquipmentTypes.getRatesByEqType(selectedAssetType, location?.equipmentTypesAll ?? []);
  const selectedEqDayRate = selectedEq?.pricePerDay ?? 0;
  const selectedEqMonthRate = selectedEq?.pricePerMonth ?? 0;

  const dayRate = selectedEqDayRate > 0 ? selectedEqDayRate : (location?.pricePerDay ?? 0);
  const monthRate = selectedEqMonthRate > 0 ? selectedEqMonthRate : (location?.pricePerMonth ?? 0);

  return monthly ? monthRate : dayRate;
};

export const isZeroPartnerPayout = (customerChargedAmountPerPeriod: number | string, supplierPayoutAmountPerPeriod: number | string): boolean => {
  const noPartnerPayout = !supplierPayoutAmountPerPeriod || supplierPayoutAmountPerPeriod === 0 || supplierPayoutAmountPerPeriod === '';
  return Number(customerChargedAmountPerPeriod) > 0 && noPartnerPayout;
}

export const isZeroSecurSpacePayout = (customerChargedAmountPerPeriod: number | string, supplierPayoutAmountPerPeriod: number | string): boolean => {
  return customerChargedAmountPerPeriod === supplierPayoutAmountPerPeriod && customerChargedAmountPerPeriod !== '' && supplierPayoutAmountPerPeriod !== '';
}

export const getAgreementUrl = (booking?: Booking): string => {
  return booking && booking.supplierLegalAgreementFileName ?
      'https://s3-us-west-1.amazonaws.com/securspace-files/legal-agreements/' + booking.supplierLegalAgreementFileName
      :
      'https://static.secur.space/app-files/RESERVATION+AGREEMENT.pdf';
}

export const startDateLessThan24Hours = (booking: Booking): boolean => {
  const ONE_HOUR = 60 * 60 * 1000;
  const TWENTY_FOUR_HOURS = 24 * ONE_HOUR;
  const timeBeforeBookingStart = new Date(booking.startDate).getTime() - new Date().getTime();

  return timeBeforeBookingStart < TWENTY_FOUR_HOURS;
}

export const scrollToTop = (): void => {
  window.scrollTo({
    top: 0,
    behavior: 'smooth'
  });
};

export const customNumberField = (value: string): string => value.replace(/\D/g, "");

export const formatBookingLocation = (booking: Booking): string => {
  return `${booking.locationAddressLine1}, ${booking.locationCity}, ${booking.locationState}, ${booking.locationZip}`;
}

const getUpdatedRecurringProperties = (
  startDate: string,
  selectedFrequencyType: typeof FREQUENCY_TYPE_WEEKLY | typeof FREQUENCY_TYPE_MONTHLY,
  maxNumberOfIterations?: number
): SelectInputProps[] => {
  const frequencyOptions = new FrequencyOptions(startDate);
  const frequencyTypeOptions = frequencyOptions.createOptions(
    selectedFrequencyType,
    maxNumberOfIterations
  );

  return frequencyTypeOptions.map(item => ({
    label: item.displayValue,
    value: item.endDate
  }));
};

export const getRecurringProperties = (durationType: string, startDate: string): SelectInputProps[] => {
  if (durationType === 'WEEKLY') {
    const diffBetweenNowAndStartDate = moment(startDate, DateFormats.DAY).diff(moment(), 'weeks');
    if (diffBetweenNowAndStartDate < 0) {
      const numberOfRecurrences = Math.abs(diffBetweenNowAndStartDate) + FREQUENCY_TYPE_WEEKLY.maxNumberOfRecurrences;
      return getUpdatedRecurringProperties(startDate, FREQUENCY_TYPE_WEEKLY, numberOfRecurrences);
    } else {
      return getUpdatedRecurringProperties(startDate, FREQUENCY_TYPE_WEEKLY);
    }
  } else {
    const diffBetweenNowAndStartDate = moment(startDate, DateFormats.DAY).diff(moment(), 'months');
    if (diffBetweenNowAndStartDate < 0) {
      const numberOfRecurrences = Math.abs(diffBetweenNowAndStartDate) + FREQUENCY_TYPE_MONTHLY.maxNumberOfRecurrences;
      return getUpdatedRecurringProperties(startDate, FREQUENCY_TYPE_MONTHLY, numberOfRecurrences);
    } else {
      return getUpdatedRecurringProperties(startDate, FREQUENCY_TYPE_MONTHLY);
    }
  }
};
